import { FC } from 'react';

import type { SanityResultBlock, SanityQuizTheme } from '@lib/sanity/types';
import type { LlmResult } from '@lib/page-utils/Quiz';

type ResultBlockProps = SanityResultBlock & {
  theme?: SanityQuizTheme;
  llmResult?: LlmResult;
  onShare: () => void;
  onNext: () => void;
};

const ShareIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

const ResultBlock: FC<ResultBlockProps> = ({
  labelText,
  primaryButtonText,
  primaryButtonIcon,
  secondaryButtonText,
  subtextBelow,
  llmResult,
  onShare,
  onNext
}) => {
  if (!llmResult) return null;

  return (
    <section className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        {/* Label */}
        <p className="mb-3 font-body text-xs font-semibold uppercase tracking-widest text-neutral-400">
          {labelText}
        </p>

        {/* Decorative line */}
        <div className="mx-auto mb-8 h-px w-16 bg-neutral-300" />

        {/* Name card */}
        <div className="mb-6 px-8 py-10 bg-neutral-800 rounded-md border border-neutral-800">
          <h2
            className="font-heading font-black text-white leading-none tracking-tight text-center"
            style={{
              fontSize: 'clamp(1.75rem, 5vw, 2.25rem)'
            }}
          >
            {llmResult.name}
          </h2>
        </div>

        {/* Description */}
        <p className="mb-10 font-heading italic text-base text-neutral-600 leading-normal">
          {llmResult.description}
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onShare}
            className="flex w-full items-center justify-center gap-2.5 font-body transition-all duration-200 hover:shadow-md py-[15px] px-6 rounded text-sm font-semibold bg-neutral-800 text-white tracking-wide"
          >
            {primaryButtonIcon === 'share' && <ShareIcon />}
            {primaryButtonText}
          </button>

          {secondaryButtonText && (
            <button
              type="button"
              onClick={onNext}
              className="w-full font-body transition-all duration-200 py-[15px] px-6 rounded text-sm font-semibold bg-transparent text-primary-600 border-2 border-primary-600 tracking-wide"
            >
              {secondaryButtonText}
            </button>
          )}
        </div>

        {subtextBelow && (
          <p className="mt-6 font-heading italic text-sm text-neutral-400">
            {subtextBelow}
          </p>
        )}
      </div>
    </section>
  );
};

export default ResultBlock;
